// ===========================================================================
// Sincronización de asignación de tareas del CRM
// ---------------------------------------------------------------------------
// Reasigna en "tiempo real" las tareas ABIERTAS al responsable correcto según
// la configuración actual (vínculo ejecutivo↔usuario y responsables de
// cobros/reclamos). Solo toca las tareas con asignación AUTOMÁTICA: las que un
// usuario reasignó a mano (asignado_manual = true) se respetan siempre.
//
// Reglas de asignación:
//   - renovacion / endoso / declaracion → usuario vinculado al ejecutivo de cuenta
//   - cobro                → ejecutivo de cobros designado (config)
//   - reclamo              → ejecutivo de reclamos designado (config)
//   - cotizacion           → usuario que creó la solicitud (creado_por)
//   - (otros tipos)        → no se auto-asignan
//
// Se llama: al generar tareas (CRM), al guardar responsables y al vincular un
// usuario con su ejecutivo (Admin). Devuelve cuántas tareas cambiaron.
// ===========================================================================
import { supabase, updateRow } from './engine';
import { getConfig } from './contabilidad';

export async function sincronizarAsignaciones() {
  const cfg = await getConfig();
  const respCobros   = cfg?.responsable_cobros_usuario_id   || null;
  const respReclamos = cfg?.responsable_reclamos_usuario_id || null;

  // Mapa ejecutivo → usuario
  const { data: ue } = await supabase.from('usuarios')
    .select('id, ejecutivo_id').eq('activo', true).not('ejecutivo_id', 'is', null);
  const ejecToUser = {};
  (ue || []).forEach(u => { ejecToUser[u.ejecutivo_id] = u.id; });

  // Tareas abiertas con asignación automática (no las reasignadas a mano)
  const { data: tasks } = await supabase.from('crm_gestiones')
    .select('id, tipo, poliza_id, solicitud_id, asignado_a, asignado_manual')
    .in('estado', ['abierto', 'en_proceso']);
  const autoTasks = (tasks || []).filter(t => !t.asignado_manual);

  // Ejecutivos por póliza (cuenta / cobro / reclamo) para renovación, endoso, cobro y reclamo
  const polIds = [...new Set(autoTasks.filter(t => ['renovacion','endoso','declaracion','cobro','reclamo'].includes(t.tipo) && t.poliza_id).map(t => t.poliza_id))];
  const polMap = {};
  if (polIds.length) {
    const { data: pols } = await supabase.from('polizas')
      .select('id, ejecutivo_id, ejecutivo_cobro_id, ejecutivo_reclamo_id').in('id', polIds);
    (pols || []).forEach(p => { polMap[p.id] = p; });
  }

  // Creador por solicitud (cotización)
  const solIds = [...new Set(autoTasks.filter(t => t.tipo === 'cotizacion' && t.solicitud_id).map(t => t.solicitud_id))];
  const solCreador = {};
  if (solIds.length) {
    const { data: sols } = await supabase.from('solicitudes').select('id, creado_por').in('id', solIds);
    (sols || []).forEach(s => { solCreador[s.id] = s.creado_por; });
  }

  // Se calcula qué tareas cambian y se aplican los updates en paralelo (no una por una).
  const pendientes = [];
  for (const t of autoTasks) {
    let target = null;
    const pol = polMap[t.poliza_id];
    if (t.tipo === 'cobro')           target = ejecToUser[pol?.ejecutivo_cobro_id]   || respCobros;
    else if (t.tipo === 'reclamo')    target = ejecToUser[pol?.ejecutivo_reclamo_id] || respReclamos;
    else if (t.tipo === 'renovacion' || t.tipo === 'endoso' || t.tipo === 'declaracion') target = ejecToUser[pol?.ejecutivo_id] || null;
    else if (t.tipo === 'cotizacion') target = solCreador[t.solicitud_id] || null;
    else continue; // obligacion / seguimiento / otro: no se auto-asignan

    // Solo actualiza cuando hay un destinatario y difiere del actual.
    // No se "limpia" a null para no borrar una asignación previa sin necesidad.
    if (target != null && String(target) !== String(t.asignado_a || '')) {
      pendientes.push(updateRow('crm_gestiones', t.id, { asignado_a: target, asignado_manual: false }));
    }
  }
  if (pendientes.length) await Promise.all(pendientes);
  return pendientes.length;
}
